import { useCallback } from 'react';

/**
 * Enhanced keyboard shortcuts hook with comprehensive key handling
 */
export const useKeyboardShortcuts = ({
  selectedNode,
  onAddChild,
  onAddSibling,
  onSelectParent,
  onFocusNode,
  onUndo,
  onRedo,
  onRemoveNode,
  onCollapseNode,
  onExpandNode,
  onFitVisible,
  onFitSelection,
  onOpenSearch,
  onFocusBack,
  onAddToParent,
  onDeleteNode
}) => {

  const handleKeyDown = useCallback((e) => {
    const isCtrl = e.ctrlKey || e.metaKey; // Support both Ctrl and Cmd
    const isShift = e.shiftKey;

    if (isCtrl && !isShift && String(e.key).toLowerCase() === 'f') {
      e.preventDefault();
      onOpenSearch?.();
      return;
    }

    // Don't handle shortcuts if user is typing in an input or textarea
    if (
      e.target.tagName === 'INPUT'
      || e.target.tagName === 'TEXTAREA'
      || e.target.tagName === 'SELECT'
    ) {
      return;
    }

    // Don't handle shortcuts if a node is currently being edited
    if (e.target.contentEditable === 'true' && e.target.isContentEditable) {
      return;
    }

    if (isShift && !isCtrl && e.code === 'Digit1') {
      e.preventDefault();
      onFitVisible?.();
      return;
    }
    if (isShift && !isCtrl && e.code === 'Digit2') {
      e.preventDefault();
      onFitSelection?.();
      return;
    }

    // Handle different key combinations
    switch (e.key) {
      case 'Tab':
        // Always prevent default for Tab to override browser behavior
        e.preventDefault();
        e.stopPropagation();
        if (isShift) {
          // Shift+Tab: add another child to the selected node's parent.
          // The root has no parent, so the shortcut intentionally does nothing.
          if (selectedNode && !selectedNode.isRoot) {
            onAddSibling?.();
          }
        } else {
          // Tab: Add child node
          onAddChild?.();
        }
        break;

      case 'Enter':
        if (!isShift && !isCtrl) {
          e.preventDefault();
          // Enter: add a sibling after the selected node.
          onAddSibling?.();
        }
        break;

      case 'F2':
      case 'e':
      case 'E':
      case ' ': // Spacebar remains an editing compatibility shortcut.
        if (!isShift && !isCtrl && selectedNode) {
          e.preventDefault();
          onFocusNode?.(selectedNode.id);
        }
        break;

      case 'Delete':
      case 'Backspace':
        if (!isCtrl && !isShift) {
          e.preventDefault();
          // Delete/Backspace: Remove selected node (only when not editing)
          onDeleteNode?.();
        }
        break;

      case 'z':
      case 'Z':
        if (isCtrl) {
          e.preventDefault();
          if (isShift) {
            // Ctrl/Cmd+Shift+Z: Redo
            onRedo?.();
          } else {
            // Ctrl/Cmd+Z: Undo
            onUndo?.();
          }
        }
        break;

      case 'y':
      case 'Y':
        if (isCtrl && !isShift) {
          e.preventDefault();
          // Ctrl/Cmd+Y: Redo compatibility shortcut
          onRedo?.();
        }
        break;

      case 'ArrowUp':
        if (!isCtrl && !isShift) {
          e.preventDefault();
          // Arrow Up: Select parent
          onSelectParent?.();
        }
        break;

      case 'ArrowDown':
        if (!isCtrl && !isShift) {
          e.preventDefault();
          // Arrow Down: Add child (same as Tab)
          onAddChild?.();
        }
        break;

      case 'ArrowLeft':
        if (isShift) {
          e.preventDefault();
          // Shift+Arrow Left: Collapse node
          onCollapseNode?.();
        }
        break;

      case 'ArrowRight':
        if (isShift) {
          e.preventDefault();
          // Shift+Arrow Right: Expand node
          onExpandNode?.();
        }
        break;

      case 'Insert':
        if (!isCtrl && !isShift) {
          e.preventDefault();
          // Insert remains a compatibility alias for adding a sibling.
          onAddSibling?.();
        }
        break;

      case 'Escape':
        if (!isCtrl && !isShift) {
          onFocusBack?.();
        }
        break;

      case '1':
      case '!':
        if (isShift && !isCtrl) {
          e.preventDefault();
          onFitVisible?.();
        }
        break;

      case '2':
      case '@':
        if (isShift && !isCtrl) {
          e.preventDefault();
          onFitSelection?.();
        }
        break;

      default:
        // Previously: starting editing on any alphanumeric key when a node
        // was selected. That swallowed the first character the user typed.
        // Editing is now initiated explicitly via double-click or Enter/Space.
        break;
    }
  }, [
    selectedNode,
    onAddChild,
    onAddSibling,
    onSelectParent,
    onFocusNode,
    onUndo,
    onRedo,
    //onRemoveNode,
    onCollapseNode,
    onExpandNode,
    onFitVisible,
    onFitSelection,
    onOpenSearch,
    onFocusBack,
    //onAddToParent,
    onDeleteNode
  ]);

  return {
    handleKeyDown
  };
};
